// Selector de mes para la página de depreciación y reportes.
//
// Dos shadcn Select separados (Año + Mes) para soportar rango histórico amplio
// sin lista interminable. El cliente piloto necesita navegar 2-5 años de
// historia (importes VAMPP desde 2023+, B44 reality check 2026-05-06).
//
// API pública sin cambios: value y onChange en formato YYYY-MM.
//
// Comportamiento intencional (B44 adversarial vector 1): permite combinar
// Año+Mes que caen en el futuro relativo a "hoy". El backend acepta cualquier
// YYYY-MM y los consumers ya validan formato; restringir aquí agregaría
// complejidad sin beneficio.

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMemo } from "react";

const MIN_YEAR = 2020;

const MONTH_LABELS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

type Props = {
  value: string; // YYYY-MM
  onChange: (next: string) => void;
};

export function MonthSelector({ value, onChange }: Props) {
  const currentYear = new Date().getFullYear();
  const valueYear = value.slice(0, 4);
  const valueMonth = value.slice(5, 7);

  const years = useMemo(() => {
    const set = new Set<number>();
    for (let y = MIN_YEAR; y <= currentYear; y++) set.add(y);
    const parsed = Number.parseInt(valueYear, 10);
    if (!Number.isNaN(parsed)) set.add(parsed);
    return Array.from(set).sort((a, b) => b - a);
  }, [currentYear, valueYear]);

  function handleYearChange(nextYear: string) {
    onChange(`${nextYear}-${valueMonth}`);
  }

  function handleMonthChange(nextMonth: string) {
    onChange(`${valueYear}-${nextMonth}`);
  }

  return (
    <div className="flex gap-2">
      <Select value={valueYear} onValueChange={handleYearChange}>
        <SelectTrigger
          aria-label="Seleccionar año"
          data-testid="depreciation-year-selector"
          className="h-10 w-[96px] text-base"
        >
          <SelectValue placeholder="Año" />
        </SelectTrigger>
        <SelectContent>
          {years.map((y) => (
            <SelectItem key={y} value={String(y)}>
              {y}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={valueMonth} onValueChange={handleMonthChange}>
        <SelectTrigger
          aria-label="Seleccionar mes"
          data-testid="depreciation-month-selector"
          className="h-10 w-[140px] text-base"
        >
          <SelectValue placeholder="Mes" />
        </SelectTrigger>
        <SelectContent>
          {MONTH_LABELS.map((label, i) => {
            const monthValue = String(i + 1).padStart(2, "0");
            return (
              <SelectItem key={monthValue} value={monthValue}>
                {label}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

export function currentYearMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}
